import React from 'react';
import ImageWrapper from '../../ImageWrapper/ImageWrapper';
import Button from '../../Button/Button';
import Switch from '../../Switch/Switch';
import Currency from '../../../config/Currency';
import CurrencyFormat from 'react-currency-format';
import changeIcon from './images/change.png';
import changeIconHover from './images/change-hover.png';
import removeIcon from './images/remove.png';
import removeIconHover from './images/remove-hover.png';
import plusIcon from './images/plus.png';
import './ComparisonProduct.css';

function ComparisonProduct({
    index,
    product,
    showTableTitle,
    showTableHeaders,
    showHightlightOption,
    highlightDifferences,
    highlightColor,
    featuresKeys,
    taxMultiplier,
    taxLabel,
    taxDisclaimerBackgroundColor,
    taxDisclaimerLabelColor,
    actionButtonBackgroundColor,
    actionButtonBorderColor,
    actionButtonLabelColor,
    secondaryActionButtonBackgroundColor,
    secondaryActionButtonBorderColor,
    secondaryActionButtonLabelColor,
    goToProductButtonIcon,
    addProductButtonIcon,
    showAIGeneratedFeatures,
    showAIGeneratedTag,
    AIGeneratedTagLabel,
    AIGeneratedTagBackgroundColor,
    AIGeneratedTagLabelColor,
    AIGeneratedTagBorderColor,
    width,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    isDifferent,
    onClick,
    onChange,
    onRemove,
    onHighlightChange,
}) {
    const [isHoveringChange, setIsHoveringChange] = React.useState(false);
    const [isHoveringRemove, setIsHoveringRemove] = React.useState(false);
    const currencyFormat = Currency.CLP;

    const handleGoToProduct = () => {
        if (product && onClick) onClick(index);
    };

    const handleChangeMouseEnter = () => {
        setIsHoveringChange(true);
    };

    const handleChangeMouseLeave = () => {
        setIsHoveringChange(false);
    };

    const handleChange = (event) => {
        if (event) event.stopPropagation();
        if (onChange) onChange(index);
    };

    const handleRemoveMouseEnter = () => {
        setIsHoveringRemove(true);
    };

    const handleRemoveMouseLeave = () => {
        setIsHoveringRemove(false);
    };

    const handleRemove = (event) => {
        event.stopPropagation();
        if (onRemove) onRemove(index);
    };

    const handleHighlightChange = (newValue) => {
        if (onHighlightChange) onHighlightChange(newValue);
    };

    const getPriceWithTaxes = (price) => {
        return taxMultiplier ? price * taxMultiplier : price;
    };

    const buildFeatures = () => {
        let features = [];
        featuresKeys.forEach((featureKey) => {
            if (product) {
                const container = featureKey.container ? product[featureKey.container] : product;
                if (featureKey.containerType === 'object') {
                    const value = container[featureKey.attribute];
                    features.push({
                        name: featureKey.name,
                        value: value ? value.toString().trim() : '-',
                        container: featureKey.container,
                        containerType: featureKey.containerType,
                        attribute: featureKey.attribute,
                        aiGenerated: false
                    });
                } else if (featureKey.containerType === 'array') {
                    const feature = container.find((feature) => feature.name === featureKey.attribute);
                    const show = feature && feature.visible && feature.value && ((feature.aiGenerated && showAIGeneratedFeatures) || !feature.aiGenerated);
                    const value = show ? feature.value.trim() : '-';
                    const aiGenerated = feature && feature.aiGenerated;
                    features.push({
                        name: featureKey.name,
                        value: value,
                        container: featureKey.container,
                        containerType: featureKey.containerType,
                        attribute: featureKey.attribute,
                        aiGenerated: aiGenerated
                    });
                }
            } else {
                features.push({
                    name: featureKey.name,
                    value: '-',
                    container: featureKey.container,
                    containerType: featureKey.containerType,
                    attribute: featureKey.attribute,
                    aiGenerated: false
                });
            }
        });
        return features;
    };

    const wrapperStyle = () => {
        return {
            width: width || 0,
            minWidth: width || 0,
            paddingTop: paddingTop || 0,
            paddingRight: paddingRight || 0,
            paddingBottom: paddingBottom || 0,
            paddingLeft: paddingLeft || 0,
            marginTop: marginTop || 0,
            marginRight: marginRight || 0,
            marginBottom: marginBottom || 0,
            marginLeft: marginLeft || 0
        };
    };

    const taxDisclaimerStyle = () => {
        return {
            background: taxDisclaimerBackgroundColor || '#005B80',
            color: taxDisclaimerLabelColor || '#FFFFFF'
        };
    };

    const featureValueStyle = (feature) => {
        return {
            background: highlightDifferences && product && isDifferent(feature) ? (highlightColor || '#FFCD29') : '#F5F6F7',
            padding: feature.aiGenerated ? '5px 40px 5px 10px' : '5px 10px'
        };
    };

    const aiGeneratedTagStyle = () => {
        return {
            backgroundColor: AIGeneratedTagBackgroundColor || '#313B45',
            color: AIGeneratedTagLabelColor || '#FFFFFF',
            border: `solid 1px ${AIGeneratedTagBorderColor || '#313B45'}`
        };
    };

    const renderFeatures = () => {
        const renderAsPrice = ['normalPrice', 'offerPrice', 'exclusivePrice'];
        const features = buildFeatures();
        return features.map((feature, featureIndex) => {
            let value = undefined;
            if (renderAsPrice.includes(feature.attribute) && !!feature.value && feature.value !== '-') {
                value = (
                    <div className='comparison-product-feature-price'>
                        <CurrencyFormat
                            prefix={currencyFormat.prefix}
                            suffix={currencyFormat.suffix}
                            thousandSeparator={currencyFormat.thousandsSeparator}
                            decimalSeparator={currencyFormat.decimalSeparator}
                            decimalScale={currencyFormat.decimalScale}
                            value={getPriceWithTaxes(feature.value)}
                            displayType='text'
                        />
                        {taxMultiplier && taxMultiplier > 1 && (
                            <div className='comparison-product-feature-price-tax-disclaimer' style={taxDisclaimerStyle()}>{taxLabel} Inc.</div>
                        )}
                    </div>
                );
            } else value = feature.value;
            return (
                <div key={`comparison-product-feature-${index}-${featureIndex}`} className={`comparison-product-feature${feature.aiGenerated ? ' ai-g' : ''}`}>
                    <div className='comparison-product-feature-key'>{showTableHeaders ? feature.name : ''}</div>
                    <div className='comparison-product-feature-value' style={featureValueStyle(feature)}>
                        {value}
                        {feature.aiGenerated && showAIGeneratedFeatures && showAIGeneratedTag && (
                            <div className='comparison-product-feature-ai-generated-tag' style={aiGeneratedTagStyle()}>{AIGeneratedTagLabel || 'IA'}</div>
                        )}
                    </div>
                </div>
            );
        });
    };

    const renderProductAction = () => {
        if (product) {
            return (
                <Button
                    icon={goToProductButtonIcon}
                    label='Ir a la ficha'
                    labelColor={actionButtonLabelColor}
                    backgroundColor={actionButtonBackgroundColor}
                    borderColor={actionButtonBorderColor}
                    borderRadius={12}
                    width='100%'
                    onClick={handleGoToProduct}
                />
            );
        } else {
            return (
                <Button
                    icon={addProductButtonIcon}
                    label='Agregar producto'
                    labelColor={secondaryActionButtonLabelColor}
                    backgroundColor={secondaryActionButtonBackgroundColor}
                    borderColor={secondaryActionButtonBorderColor}
                    borderRadius={12}
                    boxShadow='none'
                    width='100%'
                    onClick={handleChange}
                />
            );
        }
    };

    return (
        <div className='comparison-product-wrapper' style={wrapperStyle()}>
            <div className={`comparison-product${product ? '' : ' empty'}`} onClick={product ? () => {} : handleChange}>
                {product && (
                    <div className='comparison-product-first-row'>
                        <div className='comparison-product-image'>
                            <ImageWrapper image={product.getMainImage('m')} alt='Product' display='block' width={100} height='auto' loadDelay={0.2}/>
                        </div>
                        <div className='comparison-product-details'>
                            <div className='comparison-product-brand'>{product.brand || 'Sin Marca'}</div>
                            <div className='comparison-product-name'>{product.name || 'Sin Nombre'}</div>
                            <div className='comparison-product-sku'>{product.sku || 'Sin SKU'}</div>
                        </div>
                    </div>
                )}
                {!product && (
                    <div className='comparison-product-add'>
                        <ImageWrapper image={plusIcon} alt='Empty' display='block' width={100} height='auto' loadDelay={0}/>
                    </div>
                )}
                {product && (
                    <div className='comparison-product-actions'>
                        <div className='comparison-product-button' onMouseEnter={handleChangeMouseEnter} onMouseLeave={handleChangeMouseLeave} onClick={handleChange}>
                            <div className='comparison-product-button-icon'>
                                <ImageWrapper image={isHoveringChange ? changeIconHover : changeIcon} alt='Change' display='block' width={20} height='auto' loadDelay={0}/>
                            </div>
                            <span className='comparison-product-button-label'>Cambiar</span>
                        </div>
                        <div className='comparison-product-button' onMouseEnter={handleRemoveMouseEnter} onMouseLeave={handleRemoveMouseLeave} onClick={handleRemove}>
                            <div className='comparison-product-button-icon'>
                                <ImageWrapper image={isHoveringRemove ? removeIconHover : removeIcon} alt='Change' display='block' width={20} height='auto' loadDelay={0}/>
                            </div>
                            <span className='comparison-product-button-label'>Eliminar</span>
                        </div>
                    </div>
                )}
            </div>
            <div className='comparison-product-action'>{renderProductAction()}</div>
            <div className='comparison-product-row'>
                {showTableTitle && <div className='comparison-product-table-title'>Especificaciones generales</div>}
                {showHightlightOption && (
                    <div className='comparison-product-table-highlight'>
                        <div className='comparison-product-table-highlight-label'>Resaltar diferencias</div>
                        <div className='comparison-product-table-highlight-switch'>
                            <Switch checked={highlightDifferences} checkedBackgroundColor={highlightColor} onChange={handleHighlightChange}/>
                        </div>
                    </div>
                )}
                {!showTableTitle && !showHightlightOption && <div className='comparison-product-table-title'/>}
            </div>
            <div className='comparison-product-table'>
                {renderFeatures()}
            </div>
        </div>
    );
}

export default ComparisonProduct;